import { RefObject, useLayoutEffect, useState } from "react";

export interface TooltipAnchor {
  top: number;
  bottom: number;
  left: number;
  width: number;
}

export interface TooltipPosition {
  top: number;
  left: number;
  below: boolean;
}

const VIEWPORT_MARGIN = 8;
const GAP = 6;

// Centers a tooltip on `anchor`, clamped inside the viewport horizontally.
// "auto" placement prefers opening above and only flips below if there's no
// room; "below" always opens below (e.g. header cells, where "above" would
// land outside the page or overlap unrelated content).
export function useTruncateTooltipPosition(
  anchor: TooltipAnchor | null,
  tipRef: RefObject<HTMLDivElement>,
  placement: "auto" | "below"
): TooltipPosition | null {
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  useLayoutEffect(() => {
    if (!anchor || !tipRef.current) {
      setPosition(null);
      return;
    }
    const rect = tipRef.current.getBoundingClientRect();
    let left = anchor.left + anchor.width / 2 - rect.width / 2;
    left = Math.min(Math.max(left, VIEWPORT_MARGIN), window.innerWidth - rect.width - VIEWPORT_MARGIN);
    let top: number;
    let below: boolean;
    if (placement === "below") {
      below = true;
      top = anchor.bottom + GAP;
    } else {
      top = anchor.top - rect.height - GAP;
      below = top < VIEWPORT_MARGIN;
      if (below) top = anchor.bottom + GAP;
    }
    setPosition({ top, left, below });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor, placement]);

  return position;
}
